import { useEffect, useRef, useState, type ReactNode } from "react";

import { useT, type Chave } from "../i18n";
import { IconDiscord, Icone, IconWhatsApp } from "./icons";
import { useTelaEstreita } from "./tela";

/**
 * A navegação do jogo, que no celular não cabe inteira.
 *
 * São catorze destinos. No computador eles cabem na coluna da esquerda e ficam
 * todos à vista, que é o melhor jeito: nada escondido, nada a mais para tocar.
 *
 * No celular a mesma lista virava uma fileira que rolava de lado, e rolagem
 * lateral numa barra de navegação é o pior lugar possível para ela: o que está
 * fora da tela não parece existir, e quem rolou perde a referência de onde
 * estava. Então a barra fica com os quatro destinos de toda rodada e um botão
 * que abre o resto numa folha que sobe.
 *
 * Quem decide o que é "de toda rodada" é cada tela, pelo campo `principal`, e
 * não este componente: ele não sabe o que é elenco nem o que é loja.
 */

export interface DestinoDeNavegacao<T extends string> {
  chave: T;
  rotulo: Chave;
  icone: ReactNode;
  /** Quantos itens esperam decisão ali. Zero e indefinido não desenham nada. */
  aviso?: number;
  /** Fica na barra do celular. O resto vai para a folha. */
  principal?: boolean;
  /**
   * Sai do hub em vez de trocar a seção.
   *
   * Loja, ranqueado, ligas e conta são telas inteiras, com o próprio voltar.
   * Elas entram nesta lista porque é aqui que se procura por elas, e não
   * porque passaram a caber dentro do hub.
   */
  aoEscolher?: () => void;
}

interface LinkDaComunidade {
  chave: string;
  url: string;
  /** Nome da marca, que é igual nos três idiomas e por isso não passa pelo i18n. */
  nome: string;
  icone: ReactNode;
}

/**
 * Os convites da comunidade, que vêm do ambiente.
 *
 * Link de convite de grupo e de servidor vence, é revogado e troca, e nenhuma
 * dessas três coisas pode exigir uma build nova. Quem não preencheu a variável
 * não ganha um botão morto: o endereço vazio tira o botão da tela.
 */
const COMUNIDADE: LinkDaComunidade[] = [
  {
    chave: "whatsapp",
    url: import.meta.env.VITE_WHATSAPP_URL ?? "",
    nome: "WhatsApp",
    icone: <IconWhatsApp />,
  },
  {
    chave: "discord",
    url: import.meta.env.VITE_DISCORD_URL ?? "",
    nome: "Discord",
    icone: <IconDiscord />,
  },
].filter((link) => link.url.trim() !== "");

/**
 * O rodapé de botões quadrados.
 *
 * Os dois links da comunidade e a ajuda de atalhos, que estava perdida entre o
 * "Assistir" e o "Avançar rodada" na barra de ações: ali ela dividia espaço com
 * os dois botões que a pessoa aperta a cada rodada, e um "?" ao lado deles é
 * ruído em cima da ação principal.
 *
 * `aoAbrirAtalhos` só chega na coluna da esquerda. Na folha do celular ficam os
 * dois links e mais nada, porque atalho de teclado precisa de teclado.
 */
function RodapeDeLinks({
  classe,
  aoAbrirAtalhos,
}: {
  classe: string;
  aoAbrirAtalhos?: (() => void) | undefined;
}): ReactNode {
  const t = useT();
  if (COMUNIDADE.length === 0 && !aoAbrirAtalhos) return null;

  return (
    <div className={classe}>
      {COMUNIDADE.map((link) => (
        <a
          key={link.chave}
          className="link-quadrado"
          href={link.url}
          target="_blank"
          /* `noopener` porque a aba aberta ganharia acesso a esta pela `opener`. */
          rel="noreferrer noopener"
          title={link.nome}
          aria-label={link.nome}
        >
          {link.icone}
        </a>
      ))}
      {aoAbrirAtalhos && (
        <button
          className="link-quadrado"
          onClick={aoAbrirAtalhos}
          title={t("hub.atalhosDica")}
          aria-label={t("hub.atalhosAria")}
        >
          <span aria-hidden style={{ fontSize: 15, fontWeight: 800, lineHeight: 1 }}>
            ?
          </span>
        </button>
      )}
    </div>
  );
}

function Cracha({ valor }: { valor: number }): ReactNode {
  return (
    <span className="nav-soon" style={{ color: "var(--accent)", fontWeight: 800, fontSize: 11 }}>
      {valor}
    </span>
  );
}

export function BarraDeNavegacao<T extends string>({
  destinos,
  atual,
  aoTrocar,
  aria,
  aoAbrirAtalhos,
}: {
  destinos: Array<DestinoDeNavegacao<T>>;
  atual: T;
  aoTrocar: (chave: T) => void;
  aria: string;
  /** Abre a ajuda de atalhos. Sem isto, o "?" não entra no rodapé. */
  aoAbrirAtalhos?: (() => void) | undefined;
}): ReactNode {
  const t = useT();
  const estreita = useTelaEstreita();
  const [folhaAberta, definirFolhaAberta] = useState(false);
  const botaoDeMais = useRef<HTMLButtonElement>(null);
  const barra = useRef<HTMLElement>(null);

  /*
   * A altura da barra de baixo, medida, vira `--altura-da-nav`.
   *
   * A faixa do próximo jogo gruda em cima desta barra, e antes a distância era
   * um número escrito à mão que não batia com a barra de verdade: sobrava uma
   * fresta, e a área segura do iPhone mudava a conta. Medindo, a faixa encosta
   * na barra em qualquer aparelho e tamanho de fonte.
   */
  useEffect(() => {
    const raiz = document.documentElement;
    const alvo = barra.current;
    if (!estreita || !alvo) {
      raiz.style.removeProperty("--altura-da-nav");
      return;
    }
    const medir = (): void => raiz.style.setProperty("--altura-da-nav", `${String(alvo.offsetHeight)}px`);
    medir();
    const observador = new ResizeObserver(medir);
    observador.observe(alvo);
    return () => {
      observador.disconnect();
      raiz.style.removeProperty("--altura-da-nav");
    };
  }, [estreita]);

  /* Trocar de largura com a folha aberta deixaria uma folha sem botão. */
  useEffect(() => {
    if (!estreita) definirFolhaAberta(false);
  }, [estreita]);

  function escolher(destino: DestinoDeNavegacao<T>): void {
    definirFolhaAberta(false);
    if (destino.aoEscolher) {
      destino.aoEscolher();
      return;
    }
    aoTrocar(destino.chave);
  }

  function Item({ destino }: { destino: DestinoDeNavegacao<T> }): ReactNode {
    return (
      <button
        className="nav-item"
        aria-current={atual === destino.chave ? "page" : undefined}
        onClick={() => escolher(destino)}
      >
        {destino.icone}
        <span>{t(destino.rotulo)}</span>
        {destino.aviso !== undefined && destino.aviso > 0 && <Cracha valor={destino.aviso} />}
      </button>
    );
  }

  if (!estreita) {
    return (
      <nav className="sidebar" aria-label={aria}>
        <SlotDaMarca />
        <div className="sidebar-nav">
          {destinos.map((destino) => (
            <Item key={destino.chave} destino={destino} />
          ))}
        </div>
        <RodapeDeLinks classe="sidebar-links" aoAbrirAtalhos={aoAbrirAtalhos} />
      </nav>
    );
  }

  const naBarra = destinos.filter((destino) => destino.principal);
  const naFolha = destinos.filter((destino) => !destino.principal);

  /*
   * O que está escondido não pode esconder um aviso junto.
   *
   * A base avisa quantos garotos esperam decisão, e ela mora na folha. Sem
   * somar os avisos no botão de "mais", o aviso sumiria da tela justamente
   * na virada de temporada, que é quando ele existe.
   */
  const avisosEscondidos = naFolha.reduce((soma, destino) => soma + (destino.aviso ?? 0), 0);
  const algumDaFolhaEstaAberto = naFolha.some((destino) => destino.chave === atual);

  return (
    <>
      <nav className="sidebar" aria-label={aria} ref={barra}>
        <div className="sidebar-nav">
          {naBarra.map((destino) => (
            <Item key={destino.chave} destino={destino} />
          ))}
          {naFolha.length > 0 && (
            <button
              ref={botaoDeMais}
              className="nav-item"
              aria-expanded={folhaAberta}
              aria-current={algumDaFolhaEstaAberto ? "page" : undefined}
              onClick={() => definirFolhaAberta((aberta) => !aberta)}
            >
              <Icone nome="Mais" />
              <span>{t("hub.nav.mais")}</span>
              {avisosEscondidos > 0 && <Cracha valor={avisosEscondidos} />}
            </button>
          )}
        </div>
      </nav>

      {folhaAberta && (
        <FolhaDeNavegacao
          aria={aria}
          aoFechar={() => {
            definirFolhaAberta(false);
            botaoDeMais.current?.focus();
          }}
          rodape={<RodapeDeLinks classe="folha-links" />}
        >
          {naFolha.map((destino) => (
            <button
              key={destino.chave}
              className="folha-item"
              aria-current={atual === destino.chave ? "page" : undefined}
              onClick={() => escolher(destino)}
            >
              {destino.icone}
              <span className="corta">{t(destino.rotulo)}</span>
              {destino.aviso !== undefined && destino.aviso > 0 && (
                <Cracha valor={destino.aviso} />
              )}
            </button>
          ))}
        </FolhaDeNavegacao>
      )}
    </>
  );
}

/**
 * A folha que sobe, com o resto dos destinos.
 *
 * Fecha no toque fora e no `Escape`, que são as duas saídas que alguém tenta
 * antes de procurar um botão de fechar.
 */
function FolhaDeNavegacao({
  children,
  aria,
  aoFechar,
  rodape,
}: {
  children: ReactNode;
  aria: string;
  aoFechar: () => void;
  /** Fica fora da lista rolante, colado no pé da folha. */
  rodape?: ReactNode;
}): ReactNode {
  useEffect(() => {
    const aoTeclar = (evento: KeyboardEvent): void => {
      if (evento.key === "Escape") aoFechar();
    };
    window.addEventListener("keydown", aoTeclar);
    return () => window.removeEventListener("keydown", aoTeclar);
  }, [aoFechar]);

  return (
    <div className="folha-fundo" role="dialog" aria-label={aria} onClick={aoFechar}>
      <div className="folha" onClick={(evento) => evento.stopPropagation()}>
        <div className="folha-pega" aria-hidden />
        <div className="folha-lista">{children}</div>
        {rodape}
      </div>
    </div>
  );
}

/** A marca, que só existe na coluna da esquerda. */
function SlotDaMarca(): ReactNode {
  const t = useT();
  return (
    <div className="sidebar-brand">
      <div className="row" style={{ gap: 9 }}>
        {/* Decorativo: quem usa leitor de tela ouve o nome no texto ao lado. */}
        <img
          className="emblema"
          src="/arte/emblema.webp"
          alt=""
          width={34}
          height={34}
          style={{ width: 34, height: 34, flexShrink: 0 }}
        />
        <div
          style={{ fontWeight: 800, letterSpacing: "0.02em", lineHeight: 1.05, fontSize: 15 }}
        >
          {t("comum.marcaLinha1")}
          <br />
          <span style={{ fontSize: "0.68em", letterSpacing: "0.06em", color: "var(--primary)" }}>{t("comum.marcaLinha2")}</span>
        </div>
      </div>
    </div>
  );
}
